import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, storage } from "../lib/api";
import ShareButton from "../components/ShareButton";
import { Bone, MapPin, Calendar, Check, ArrowLeft } from "lucide-react";

export default function CouponDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [coupon, setCoupon] = useState(null);
  const [claim, setClaim] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    api.getCoupon(id).then(setCoupon).catch(() => {});
    const claimed = storage.getClaimed().find((c) => c.coupon_id === id);
    if (claimed) {
      api.getClaimed(claimed.id).then(setClaim).catch(() => {});
    }
  }, [id]);

  const handleClaim = async () => {
    setClaiming(true);
    setBlocked(null);
    try {
      const res = await api.claimCoupon(id, { user_handle: storage.getHandle() || "anonimo" });
      storage.saveClaimed(res);
      setClaim(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.type === "already_redeemed") {
        setBlocked(detail);
      } else if (detail && typeof detail === "object" && detail.type === "already_claimed") {
        if (detail.claim_id) {
          api.getClaimed(detail.claim_id).then(setClaim).catch(() => {});
        }
      } else {
        alert(typeof detail === "string" ? detail : t("huesos.noClaimError"));
      }
    } finally {
      setClaiming(false);
    }
  };

  const inviteFriend = () => {
    const msg = `Hey, súmate a Kiltro Scout 🍺🦴 → https://www.kiltroscout.cl/cupones/${id}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  if (!coupon) {
    return (
      <div className="max-w-3xl mx-auto p-12">
        <div className="beer-pour" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8" data-testid="hueso-detail-page">
      <Link to="/cupones" className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#FEBA01] font-mono text-xs uppercase tracking-widest">
        <ArrowLeft size={14} /> {t("huesos.backToHuesos")}
      </Link>

      <div className="mt-6 bg-[#FEBA01] text-black p-8 lg:p-10 rounded-md relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-2 font-mono text-xs uppercase tracking-widest opacity-80">
          <Bone size={14} strokeWidth={2.2} /> {t("huesos.huesoKiltro")}
          {coupon.sponsored && <span className="bg-black text-[#FEBA01] px-2 py-0.5 ml-2 border border-[#FEBA01]/30">{t("huesos.sponsoredTag")}</span>}
        </div>
        <h1 className="relative z-10 mt-3 font-display text-4xl sm:text-5xl uppercase leading-[0.95] tracking-tighter">
          {coupon.title}
        </h1>
        <div className="relative z-10 mt-4 inline-block bg-black text-[#FEBA01] px-4 py-2 font-display text-3xl uppercase tracking-tighter">
          {coupon.discount}
        </div>
        <p className="relative z-10 mt-5 text-base leading-relaxed font-medium">{coupon.description}</p>
        <div className="relative z-10 mt-4 flex flex-wrap gap-4 text-xs font-mono uppercase tracking-widest opacity-90">
          <Link to={`/local/${coupon.venue_id}`} className="flex items-center gap-1 hover:underline">
            <MapPin size={12} /> {coupon.venue_name}
          </Link>
          {coupon.valid_until && (
            <span className="flex items-center gap-1">
              <Calendar size={12} /> {t("huesos.until")} {coupon.valid_until}
            </span>
          )}
        </div>
      </div>

      {coupon.terms && (
        <div className="mt-4 p-4 bg-[#1A1A1A] border-2 border-[#333] text-xs text-[#A3A3A3] font-mono uppercase tracking-wider">
          <span className="text-[#FEBA01]">{t("huesos.termsLabel")}</span> {coupon.terms}
        </div>
      )}

      <div className="mt-4">
        <ShareButton kind="coupon" id={coupon.id} label={t("huesos.shareHueso")} testid="hueso-share-btn" />
      </div>

      <div className="mt-6">
        {blocked ? (
          <BlockedMessage blocked={blocked} onInvite={inviteFriend} t={t} locale={i18n.language} />
        ) : !claim ? (
          <button
            onClick={handleClaim}
            disabled={claiming}
            className="btn-brutal w-full bg-[#FEBA01] text-black font-display text-2xl uppercase py-4 shadow-brutal-white disabled:opacity-50"
            data-testid="hueso-claim-btn"
          >
            {claiming ? t("huesos.claimGenerating") : t("huesos.claim")}
          </button>
        ) : (
          <ClaimedView claim={claim} t={t} />
        )}
      </div>
    </div>
  );
}

function BlockedMessage({ blocked, onInvite, t, locale }) {
  const localeMap = { es: "es-CL", pt: "pt-BR", en: "en-US" };
  const formattedDate = blocked.redeemed_at
    ? new Date(blocked.redeemed_at).toLocaleString(localeMap[locale] || "es-CL", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })
    : "";
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#FEBA01] p-6 text-center" data-testid="blocked-view">
      <div className="text-4xl mb-2">😮🦴</div>
      <div className="font-display text-2xl uppercase tracking-tight mb-2 text-[#FEBA01]">
        {blocked.message || t("huesos.alreadyClaimedTitle")}
      </div>
      {formattedDate && (
        <div className="text-sm text-[#A3A3A3] mb-3">
          {t("huesos.claimedOn")} <strong className="text-white">{formattedDate}</strong>
          {blocked.venue_name && <> {t("huesos.claimedAt")} <strong className="text-white">{blocked.venue_name}</strong></>}
        </div>
      )}
      <p className="text-sm text-[#E5E5E5] mb-4 max-w-sm mx-auto leading-relaxed">
        {t("huesos.newHuesosSoon")}
        <br />
        {t("huesos.inviteHint")}
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          type="button"
          onClick={onInvite}
          className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-black font-display uppercase text-sm px-5 py-3"
          data-testid="invite-friend-btn"
        >
          {t("huesos.inviteFriend")}
        </button>
        <Link
          to="/cupones"
          className="inline-flex items-center justify-center gap-2 border-2 border-[#FEBA01] text-[#FEBA01] font-display uppercase text-sm px-5 py-3 hover:bg-[#FEBA01] hover:text-black"
        >
          {t("huesos.findOthers")}
        </Link>
      </div>
    </div>
  );
}

function ClaimedView({ claim, t }) {
  return (
    <div className="bg-[#1A1A1A] border-2 border-[#FEBA01] p-6 text-center" data-testid="claimed-view">
      <div className="inline-flex items-center gap-2 bg-[#00FF66] text-black px-3 py-1 font-display uppercase tracking-tight text-sm">
        <Check size={14} /> {t("huesos.claimedBadge")}
      </div>
      {claim.qr_data_url && (
        <div className="mt-4 inline-block bg-[#0A0A0A] p-4 border-2 border-[#FEBA01]">
          <img src={claim.qr_data_url} alt={t("huesos.claimCodeLabel")} className="w-56 h-56 mx-auto" data-testid="hueso-code-image" />
        </div>
      )}
      <div className="mt-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#A3A3A3]">{t("huesos.claimCodeLabel")}</div>
        <div className="font-display text-3xl text-[#FEBA01] tracking-tight" data-testid="claim-code">{claim.claim_code}</div>
      </div>
      {!claim.redeemed && (
        <p className="mt-4 text-sm text-[#A3A3A3]">{t("huesos.claimShowInVenue")}</p>
      )}
    </div>
  );
}
